import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import WarningIcon from '@mui/icons-material/esm/Warning';
import ScheduleIcon from '@mui/icons-material/esm/Schedule';
import SettingsIcon from '@mui/icons-material/esm/Settings';
import type { Task } from '../../services/api/tasksService';

interface Props {
  tasks: Task[];
}

interface BlockerRow {
  category: 'Blocked' | 'Awaiting inspection' | 'Awaiting approval';
  taskTitle: string;
  detail: string;
  icon: JSX.Element;
}

const buildBlockers = (tasks: Task[]): BlockerRow[] => {
  const rows: BlockerRow[] = [];
  for (const t of tasks) {
    if (t.status === 'BLOCKED_DEPENDENCY' || t.status === 'BLOCKED_SHORTAGE') {
      rows.push({
        category: 'Blocked',
        taskTitle: t.title,
        detail:
          t.status === 'BLOCKED_DEPENDENCY'
            ? 'Predecessor not complete'
            : 'Resource shortage',
        icon: <WarningIcon color="error" />,
      });
    } else if (t.status === 'INSPECTION_PENDING') {
      rows.push({
        category: 'Awaiting inspection',
        taskTitle: t.title,
        detail: 'Inspection scheduled',
        icon: <ScheduleIcon color="warning" />,
      });
    } else if (t.status === 'PAYMENT_PENDING' || t.status === 'WORK_COMPLETE') {
      rows.push({
        category: 'Awaiting approval',
        taskTitle: t.title,
        detail: t.status === 'PAYMENT_PENDING' ? 'Payment approval' : 'Sign-off pending',
        icon: <SettingsIcon color="info" />,
      });
    }
  }
  return rows;
};

export default function BlockersPanel({ tasks }: Props) {
  const blockers = buildBlockers(tasks);

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">Blockers & approvals</Typography>
        <Chip
          size="small"
          color={blockers.length > 0 ? 'error' : 'success'}
          label={`${blockers.length} item${blockers.length === 1 ? '' : 's'}`}
        />
      </Box>
      {blockers.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
          No blockers in the current window.
        </Box>
      ) : (
        <List dense>
          {blockers.slice(0, 10).map((b, i) => (
            <ListItem key={`${b.taskTitle}-${i}`}>
              <ListItemIcon>{b.icon}</ListItemIcon>
              <ListItemText
                primary={b.taskTitle}
                secondary={`${b.category} — ${b.detail}`}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
}
